import React, { useState, useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import UserPreviewModal from '../common/UserPreviewModal';
import ClientPreviewModal from '../common/ClientPreviewModal';
import DealPreviewModal from '../common/DealPreviewModal';
import {
  XMarkIcon,
  CheckCircleIcon,
  PencilIcon,
  TrashIcon,
  ChatBubbleLeftIcon,
  ClockIcon,
  UserGroupIcon,
  TagIcon,
  BuildingOfficeIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import { Task, TaskComment, TaskStatus } from '../../types/task.types';
import taskService, { CreateTaskCommentDto } from '../../services/taskService';
import { format } from 'date-fns';
import TaskFormModal from './TaskFormModal';

interface TaskDetailSlideOverProps {
  task: Task;
  onClose: () => void;
  onTaskUpdated: () => void;
}

const TaskDetailSlideOver: React.FC<TaskDetailSlideOverProps> = ({ task: initialTask, onClose, onTaskUpdated }) => {
  const [task, setTask] = useState<Task>(initialTask);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Preview modal states
  const [showUserPreview, setShowUserPreview] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: number; name: string; email: string } | null>(null);
  const [showClientPreview, setShowClientPreview] = useState(false);
  const [selectedClient, setSelectedClient] = useState<{ id: number; name: string; email?: string; phone?: string[] } | null>(null);
  const [showDealPreview, setShowDealPreview] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<{ id: number; description: string; amount: number; currency: string; clientName?: string } | null>(null);

  useEffect(() => {
    loadComments();
  }, [task.id]);

  const loadComments = async () => {
    try {
      const data = await taskService.getComments(task.id);
      setComments(data);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleComplete = async () => {
    try {
      const updated = await taskService.completeTask(task.id);
      setTask(updated);
      onTaskUpdated();
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleReopen = async () => {
    try {
      const updated = await taskService.reopenTask(task.id);
      setTask(updated);
      onTaskUpdated();
    } catch (error) {
      console.error('Error reopening task:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await taskService.deleteTask(task.id);
      onTaskUpdated();
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setLoading(true);
    try {
      const comment = await taskService.addComment(task.id, {
        body: newComment,
        isInternal: true,
      });
      setComments([comment, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Error adding comment:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskEdited = () => {
    setShowEditModal(false);
    onTaskUpdated();
    // Reload task details
    taskService.getTask(task.id).then(setTask);
  };

  // Preview modal handlers
  const handleUserClick = (user: { id: number; email: string; firstName?: string; lastName?: string }) => {
    const userName = user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email;
    setSelectedUser({ id: user.id, name: userName, email: user.email });
    setShowUserPreview(true);
  };

  const handleClientClick = (client: { id: number; name: string; email?: string; phoneNumbers?: string[] }) => {
    setSelectedClient({
      id: client.id,
      name: client.name,
      email: client.email,
      phone: client.phoneNumbers
    });
    setShowClientPreview(true);
  };

  const handleDealClick = (deal: { id: number; description: string; amount: number; currency: string; client?: { name: string } }) => {
    setSelectedDeal({
      id: deal.id,
      description: deal.description,
      amount: deal.amount,
      currency: deal.currency,
      clientName: deal.client?.name
    });
    setShowDealPreview(true);
  };

  return (
    <>
      <Transition appear show={true} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-300"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-300"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                    <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                      {/* Header */}
                      <div className="bg-primary-600 px-6 py-6">
                        <div className="flex items-start justify-between">
                          <Dialog.Title className="text-xl font-semibold text-white">
                            Task Details
                          </Dialog.Title>
                          <button
                            onClick={onClose}
                            className="text-primary-200 hover:text-white"
                          >
                            <XMarkIcon className="h-6 w-6" />
                          </button>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 px-6 py-6">
                        {/* Title */}
                        <h2 className="text-2xl font-bold text-neutral-900 mb-4">{task.title}</h2>

                        {/* Action Buttons */}
                        <div className="flex gap-2 mb-6">
                          {task.status !== TaskStatus.COMPLETED ? (
                            <button
                              onClick={handleComplete}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-success-600 hover:bg-success-700"
                            >
                              <CheckCircleIcon className="h-5 w-5 mr-2" />
                              Mark Complete
                            </button>
                          ) : (
                            <button
                              onClick={handleReopen}
                              className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                            >
                              Reopen Task
                            </button>
                          )}
                          <button
                            onClick={() => setShowEditModal(true)}
                            className="inline-flex items-center px-3 py-2 border border-neutral-300 text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                          >
                            <PencilIcon className="h-5 w-5 mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={handleDelete}
                            className="inline-flex items-center px-3 py-2 border border-error-300 text-sm font-medium rounded-md text-error-700 bg-white hover:bg-error-50"
                          >
                            <TrashIcon className="h-5 w-5 mr-2" />
                            Delete
                          </button>
                        </div>

                        {/* Task Details Grid */}
                        <div className="grid grid-cols-2 gap-6 mb-8">
                          <DetailItem label="Type" value={task.type.replace('_', ' ')} />
                          <DetailItem label="Priority" value={task.priority} />
                          <DetailItem label="Status" value={task.status.replace('_', ' ')} />
                          <DetailItem 
                            label="Due Date" 
                            value={task.dueDateTime ? format(new Date(task.dueDateTime), 'MMM d, yyyy h:mm a') : 'No due date'} 
                          />
                          <DetailItem 
                            label="Created" 
                            value={format(new Date(task.createdAt), 'MMM d, yyyy')} 
                          />
                          {task.completedAt && (
                            <DetailItem 
                              label="Completed" 
                              value={format(new Date(task.completedAt), 'MMM d, yyyy h:mm a')} 
                            />
                          )}
                        </div>

                        {/* Description */}
                        {task.description && (
                          <div className="mb-8">
                            <h3 className="text-sm font-medium text-neutral-500 mb-2">Description</h3>
                            <p className="text-neutral-900 whitespace-pre-wrap">{task.description}</p>
                          </div>
                        )}

                        {/* Assigned Users */}
                        {task.assignments.length > 0 && (
                          <div className="mb-8">
                            <h3 className="text-sm font-medium text-neutral-500 mb-3 flex items-center">
                              <UserGroupIcon className="h-5 w-5 mr-2" />
                              Assigned To
                            </h3>
                            <div className="space-y-2">
                              {task.assignments.map((assignment) => (
                                <button
                                  key={assignment.id}
                                  onClick={() => handleUserClick(assignment.user)}
                                  className="flex items-center w-full p-2 rounded-lg hover:bg-neutral-50 transition-colors text-left"
                                >
                                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                    <span className="text-sm font-medium text-primary-600">
                                      {assignment.user.firstName?.[0] || assignment.user.email[0].toUpperCase()}
                                    </span>
                                  </div>
                                  <div className="ml-3">
                                    <p className="text-sm font-medium text-neutral-900">
                                      {assignment.user.firstName && assignment.user.lastName
                                        ? `${assignment.user.firstName} ${assignment.user.lastName}`
                                        : assignment.user.email}
                                      {assignment.isPrimary && (
                                        <span className="ml-2 text-xs text-primary-600">(Primary)</span>
                                      )}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Tags */}
                        {task.tags.length > 0 && (
                          <div className="mb-8">
                            <h3 className="text-sm font-medium text-neutral-500 mb-3 flex items-center">
                              <TagIcon className="h-5 w-5 mr-2" />
                              Tags
                            </h3>
                            <div className="flex flex-wrap gap-2">
                              {task.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-neutral-100 text-neutral-800"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Related Clients */}
                        {task.taskClients && task.taskClients.length > 0 && (
                          <div className="mb-8">
                            <h3 className="text-sm font-medium text-neutral-500 mb-3 flex items-center">
                              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                              Related Clients
                            </h3>
                            <div className="space-y-2">
                              {task.taskClients.map((tc) => (
                                <button
                                  key={tc.id}
                                  onClick={() => handleClientClick(tc.client)}
                                  className="flex items-center p-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors w-full text-left"
                                >
                                  <BuildingOfficeIcon className="h-5 w-5 text-primary-600 mr-3" />
                                  <div>
                                    <p className="text-sm font-medium text-neutral-900">{tc.client.name}</p>
                                    {tc.client.email && (
                                      <p className="text-xs text-neutral-500">{tc.client.email}</p>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Related Deals */}
                        {task.taskDeals && task.taskDeals.length > 0 && (
                          <div className="mb-8">
                            <h3 className="text-sm font-medium text-neutral-500 mb-3 flex items-center">
                              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                              Related Deals
                            </h3>
                            <div className="space-y-2">
                              {task.taskDeals.map((td) => (
                                <button
                                  key={td.id}
                                  onClick={() => handleDealClick(td.deal)}
                                  className="flex items-center p-3 bg-success-50 rounded-lg hover:bg-success-100 transition-colors w-full text-left"
                                >
                                  <CurrencyDollarIcon className="h-5 w-5 text-success-600 mr-3" />
                                  <div>
                                    <p className="text-sm font-medium text-neutral-900">{td.deal.description}</p>
                                    <p className="text-xs text-neutral-500">
                                      ${td.deal.amount.toLocaleString()} {td.deal.currency}
                                      {td.deal.client && ` • ${td.deal.client.name}`}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Comments Section */}
                        <div className="border-t pt-6">
                          <h3 className="text-lg font-medium text-neutral-900 mb-4 flex items-center">
                            <ChatBubbleLeftIcon className="h-5 w-5 mr-2" />
                            Comments ({comments.length})
                          </h3>

                          {/* Add Comment Form */}
                          <form onSubmit={handleAddComment} className="mb-6">
                            <textarea
                              rows={3}
                              value={newComment}
                              onChange={(e) => setNewComment(e.target.value)}
                              placeholder="Add a comment..."
                              className="block w-full rounded-md border-neutral-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                            />
                            <div className="mt-2 flex justify-end">
                              <button
                                type="submit"
                                disabled={loading || !newComment.trim()}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
                              >
                                Add Comment
                              </button>
                            </div>
                          </form>

                          {/* Comments List */}
                          <div className="space-y-4">
                            {comments.map((comment) => (
                              <div key={comment.id} className="bg-neutral-50 rounded-lg p-4">
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                                      <span className="text-sm font-medium text-primary-600">
                                        {comment.author.firstName?.[0] || comment.author.email[0].toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="ml-3">
                                      <p className="text-sm font-medium text-neutral-900">
                                        {comment.author.firstName && comment.author.lastName
                                          ? `${comment.author.firstName} ${comment.author.lastName}`
                                          : comment.author.email}
                                      </p>
                                      <p className="text-xs text-neutral-500">
                                        {format(new Date(comment.createdAt), 'MMM d, yyyy h:mm a')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                <p className="text-sm text-neutral-700 whitespace-pre-wrap">{comment.body}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Modal */}
      {showEditModal && (
        <TaskFormModal
          task={task}
          onClose={() => setShowEditModal(false)}
          onSuccess={handleTaskEdited}
        />
      )}

      {/* Preview Modals */}
      {selectedUser && (
        <UserPreviewModal
          userId={selectedUser.id}
          userName={selectedUser.name}
          userEmail={selectedUser.email}
          isOpen={showUserPreview}
          onClose={() => setShowUserPreview(false)}
        />
      )}

      {selectedClient && (
        <ClientPreviewModal
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          clientEmail={selectedClient.email}
          clientPhone={selectedClient.phone}
          isOpen={showClientPreview}
          onClose={() => setShowClientPreview(false)}
        />
      )}

      {selectedDeal && (
        <DealPreviewModal
          dealId={selectedDeal.id}
          dealDescription={selectedDeal.description}
          dealAmount={selectedDeal.amount}
          dealCurrency={selectedDeal.currency}
          clientName={selectedDeal.clientName}
          isOpen={showDealPreview}
          onClose={() => setShowDealPreview(false)}
        />
      )}
    </>
  );
};

const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <dt className="text-sm font-medium text-neutral-500">{label}</dt>
    <dd className="mt-1 text-sm text-neutral-900">{value}</dd>
  </div>
);

export default TaskDetailSlideOver;

